import { useState, useEffect, useCallback, lazy, Suspense } from 'react';

import { useAppStore, setAppStore, type IAppStore } from '@/store';
import { useProjectInit } from '@/hooks/useProjectInit';
import { useNotificationHandler } from '@/hooks/useNotificationHandler';
import { useCategoryDeeplink } from '@/hooks/useCategoryDeeplink';
import { usePresetDeeplink } from '@/hooks/usePresetDeeplink';
import { useAutoGeneration } from '@/hooks/useAutoGeneration';
import { useGenerationRecovery } from '@/hooks/useGenerationRecovery';
import { useCreditsSubscription } from '@/hooks/useCreditsSubscription';
import { useLocalNotification } from '@/hooks/useLocalNotification';
import { useLibrary } from '@/hooks/useLibrary';
import { useBrowserHistory, pushBrowserNav } from '@/hooks/useBrowserHistory';
import Header from '@/components/Header';
import Toast from '@/components/shared/Toast';
import { ResultOverlay } from './ResultOverlay';
import { useAppContentStyles } from './styles';
import { isDesktop } from '@/consts/device';
import { removeTaskIdFromUrl } from '@/utils/ui/removeTaskIdFromUrl';
import { SettingsPanel } from '@/components/Settings/SettingsPanel';
import { useUserProfile } from '@/hooks/useUserProfile';
import { resetCanvasLoadState } from '@/utils/canvas/canvasLoadState';
import { closeMiniapp } from '@/utils/editor/closeAndNavigate';
import { sendAiGenScreenActionAnalytics } from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import { getScreenType } from '@/utils/analytics/getScreenType';
import { AiGenScreenAction } from '@/types/analytics';
import AppLoader from '../shared/AppLoader';

const DesktopLayout = lazy(() =>
  import('./DesktopLayout').then((m) => ({ default: m.DesktopLayout }))
);
const MobileLayout = lazy(() =>
  import('./MobileLayout').then((m) => ({ default: m.MobileLayout }))
);

// External selector for stable reference
const selector = (store: IAppStore) => ({
  screen: store.screen,
  setScreen: store.setScreen,
  resetCreate: store.resetCreate,
  rightPanelTab: store.rightPanelTab,
  setRightPanelTab: store.setRightPanelTab,
  viewCrop: store.viewCrop,
  setViewCrop: store.setViewCrop,
  viewSize: store.viewSize,
  setViewSize: store.setViewSize,
  globalToast: store.toast,
});

export const AppContent = () => {
  const classes = useAppContentStyles();
  const [showMobileSettings, setShowMobileSettings] = useState(false);
  const { isAuthorized: userIsAuthorized } = useUserProfile();
  const {
    screen,
    setScreen,
    resetCreate,
    rightPanelTab,
    setRightPanelTab,
    viewCrop,
    setViewCrop,
    viewSize,
    setViewSize,
    globalToast,
  } = useAppStore(selector);

  useCategoryDeeplink();
  usePresetDeeplink();
  const { isLoading: isProjectLoading } = useProjectInit();
  useCreditsSubscription();
  useAutoGeneration();
  useGenerationRecovery();
  // Initialize library and auto-select preset on desktop
  useLibrary();
  const { notificationHandler, hasTaskId } = useNotificationHandler();
  const { toast: notification, hideToast: hideNotification } =
    useLocalNotification();

  // Browser history back button handling (Android WebView)
  const handleBackToLibrary = useCallback(() => {
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.BACK,
      screen_type: getScreenType(),
    });
    resetCanvasLoadState();
    setAppStore({ shouldScrollToActiveTab: true });
    setScreen('library');
  }, [setScreen]);

  const handleBackFromFiles = useCallback(() => {
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.BACK,
      screen_type: getScreenType(),
    });
    const { previousScreen } = useAppStore.getState();
    if (previousScreen && previousScreen !== 'files') {
      setScreen(previousScreen);
    } else {
      setAppStore({ shouldScrollToActiveTab: true });
      setScreen('library');
    }
  }, [setScreen]);

  const handleBackFromLightbox = useCallback(() => {
    // Dispatch custom event so Generations can close its local lightbox state
    window.dispatchEvent(new Event('backbutton:close-lightbox'));
  }, []);

  const handleBackFromSettings = useCallback(() => {
    setShowMobileSettings(false);
  }, []);

  const handleCloseApp = useCallback(() => {
    try {
      closeMiniapp();
    } catch {
      // SDK may not be available
    }
  }, []);

  useBrowserHistory({
    screen,
    onBackToLibrary: handleBackToLibrary,
    onBackFromFiles: handleBackFromFiles,
    onBackFromLightbox: handleBackFromLightbox,
    onBackFromSettings: handleBackFromSettings,
    onCloseApp: handleCloseApp,
  });

  useEffect(() => {
    if (hasTaskId) notificationHandler();
  }, [hasTaskId, notificationHandler]);

  const handleCloseResult = useCallback(() => {
    resetCreate();
    setScreen('files');
    removeTaskIdFromUrl();
  }, [resetCreate, setScreen]);

  if (isProjectLoading) {
    return <AppLoader />;
  }

  const showOverlay = screen === 'loading';

  return (
    <>
      <div className={classes.container}>
        <Header
          onSettingsClick={() => {
            setShowMobileSettings(true);
            pushBrowserNav('settings');
          }}
        />
        <main className={classes.main}>
          {showMobileSettings && !isDesktop ? (
            <SettingsPanel
              variant="page"
              onBack={() => setShowMobileSettings(false)}
              isAuthorized={userIsAuthorized}
            />
          ) : (
            <Suspense fallback={null}>
              {isDesktop ? (
                <DesktopLayout
                  rightPanelTab={rightPanelTab}
                  setRightPanelTab={setRightPanelTab}
                  viewCrop={viewCrop}
                  setViewCrop={setViewCrop}
                  viewSize={viewSize}
                  setViewSize={setViewSize}
                />
              ) : (
                <MobileLayout />
              )}
            </Suspense>
          )}
        </main>
        {showOverlay && <ResultOverlay onClose={handleCloseResult} />}
        {/* Local notification toast (with action) takes priority over global toast */}
        {notification ? (
          <Toast
            message={notification.message}
            type={notification.type}
            action={notification.action}
            onActionClick={hideNotification}
          />
        ) : (
          globalToast && (
            <Toast message={globalToast.message} type={globalToast.type} />
          )
        )}
      </div>
    </>
  );
};
